/**
 * ProfileSection - User profile display with avatar and info cards
 * Design: Modern profile header with glassmorphism elements
 */
import React from 'react';

/**
 * Generate initials from name
 */
function getInitials(firstName, lastName) {
    const first = firstName?.[0] || '';
    const last = lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
}

/**
 * Calculate member tenure
 */
function getMemberTenure(createdAt) {
    if (!createdAt) return 'Member';

    const created = new Date(createdAt);
    const now = new Date();
    const months = Math.floor((now - created) / (1000 * 60 * 60 * 24 * 30));

    if (months < 1) return 'New member';
    if (months < 12) return `Member for ${months} month${months !== 1 ? 's' : ''}`;

    const years = Math.floor(months / 12);
    return `Member for ${years} year${years !== 1 ? 's' : ''}`;
}

export default function ProfileSection({ user }) {
    if (!user) return null;

    const initials = getInitials(user.firstName, user.lastName);
    const tenure = getMemberTenure(user.createdAt);
    const joinDate = user.createdAt
        ? new Date(user.createdAt).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
        })
        : 'Unknown';

    return (
        <section id="profile" className="settings-section">
            <div className="settings-section-header">
                <span className="settings-section-icon">👤</span>
                <h2 className="settings-section-title">Profile</h2>
            </div>

            {/* Profile Header with Avatar */}
            <div className="profile-header">
                <div className="profile-avatar-wrapper">
                    <div className="profile-avatar">
                        {user.avatar ? (
                            <img src={user.avatar} alt={`${user.firstName}'s avatar`} />
                        ) : (
                            initials
                        )}
                    </div>
                    <button
                        className="profile-avatar-edit"
                        aria-label="Change avatar"
                        title="Change avatar (coming soon)"
                    >
                        📷
                    </button>
                </div>
                <div className="profile-info">
                    <h3 className="profile-name">
                        {user.firstName} {user.lastName}
                    </h3>
                    <div className="profile-email">{user.email}</div>
                    <span className="profile-badge">
                        ✨ {tenure}
                    </span>
                </div>
            </div>

            {/* Profile Details Grid */}
            <div className="profile-details-grid">
                <div className="profile-detail-card">
                    <div className="profile-detail-label">Screen Name</div>
                    <div className="profile-detail-value">
                        {user.screenName || 'Not set'}
                    </div>
                </div>
                <div className="profile-detail-card">
                    <div className="profile-detail-label">Email</div>
                    <div className="profile-detail-value">{user.email}</div>
                </div>
                <div className="profile-detail-card">
                    <div className="profile-detail-label">Member Since</div>
                    <div className="profile-detail-value">{joinDate}</div>
                </div>
                <div className="profile-detail-card">
                    <div className="profile-detail-label">Account Type</div>
                    <div className="profile-detail-value">
                        {user.role === 'admin' ? '👑 Administrator' : '👤 Member'}
                    </div>
                </div>
            </div>
        </section>
    );
}
